import { configuratorConstants, ExtraChargeType, ProductTypes, ReferenceUnit } from "$lib/constants";
import type { Product } from "$lib/server/database/drizzle-schemas";
import { useConfiguratorStore, type RoofSurface, type RoofSurfaces } from "$lib/store/configurator.store";
import { useConfiguratorProductsStore } from "$lib/store/configurator.products.store";
import { get } from "svelte/store";
import type { BatteryData } from "$lib/products/batteries";
import type { ModuleData } from "$lib/products/modules";
import { useFullFeedIn } from "$lib/store/fullFeedIn.store";

export function calculateDefaultItemsPrices(products: Array<Product>, amountOfModule: number, pvSize: number) {
    const defaultProducts = products.filter(
        (x) =>
            x.active &&
            [ProductTypes.GENERAL].includes(x.type)
    );


    let final = 0;
    defaultProducts.forEach(p => {
        const isUseGlobalSurchage = (p.data as { useGlobalSurchage: boolean }).useGlobalSurchage ?? true;
        const surcharge = isUseGlobalSurchage ? p.surchargePercentage ?? 0 : 0;
        let addedPrice = 0;
        const referenceUnit = p.referenceUnit;
        const price = p.price ?? 0;
        if (referenceUnit) {
            if (referenceUnit == ReferenceUnit.UNIT) {
                const defaultQuantity = p.defaultQuantity ?? 0;
                const useModuleAmount = (p.data as { useModuleAmount: boolean }).useModuleAmount ?? false;
                if (useModuleAmount) {
                    addedPrice = price * amountOfModule;
                } else {
                    addedPrice = price * defaultQuantity;
                }
            }
            if (referenceUnit == ReferenceUnit.KWP) {
                const data = p.data;
                const ranges = (data as { range?: Array<{ min?: number; max?: number; price?: number; surcharge?: number }> }).range ?? [];
                if (ranges.length == 0) {
                    addedPrice = price * pvSize;
                } else {
                    const range = ranges.find(range => {
                        const { min, max } = range;
                        const aboveMin = min === undefined || min === null || min === 0 || pvSize >= min;
                        const belowMax = max === undefined || max === null || max === 0 || pvSize <= max;
                        return aboveMin && belowMax;
                    });
                    const rangeSurcharge = range?.surcharge ?? 0;
                    addedPrice = (range?.price ?? price) * pvSize * (1 + (!isUseGlobalSurchage ? rangeSurcharge / 100 : 0));
                }
            }
        }
        final += addedPrice * (1 + surcharge / 100)
    })
    return final;
}

export function calculateSurcharges(products: Array<Product>, sum: number) {
    const defaultProducts = products.filter(
        (x) =>
            x.active &&
            [ProductTypes.SURCHARGE].includes(x.type)
    );
    let addedPrice = 0;
    defaultProducts.forEach(p => {
        const surcharge = p.surchargePercentage ?? 0;
        addedPrice += sum * surcharge / 100;
    })
    return addedPrice;
}

export function calculateStoragePrice(battery: Product, amountOfBattery: number, isFullFeedIn: boolean) {
    if (isFullFeedIn || amountOfBattery <= 0) return 0;
    let batSystemPrice = 0;
    if (battery && !isFullFeedIn) {
        const batteryData = battery.data as BatteryData;
        const investerPrice = batteryData.investerPrice ?? 0;
        const installationPrice = batteryData.installationPrice ?? 0;
        const addonPackagePrice = batteryData.addonPackagePrice ?? 0;
        const unitPrice = battery?.price ?? 0;
        batSystemPrice =
            unitPrice * amountOfBattery + investerPrice + installationPrice + addonPackagePrice;

    }
    batSystemPrice = batSystemPrice * (1 + (battery?.surchargePercentage ?? 0) / 100);
    return batSystemPrice;
}

export function calculateFinalPrice(initialValues?: Array<Product>) {
    const productsStore = useConfiguratorProductsStore(initialValues);
    const products = get(productsStore);
    const modules = products.filter((x) => x.type == ProductTypes.MODULE);
    const batteries = products.filter((x) => x.type == ProductTypes.BATTERY);
    const configurator = get(useConfiguratorStore());
    const isFullFeedIn = get(useFullFeedIn());

    const module = modules.find((m) => m.id === configurator.moduleComponent.moduleType);

    if (!module) {
        return;
    }
    const battery = batteries.find((m) => m.id === configurator.moduleComponent.batterySystem);
    const amountOfBattery = configurator.amountOfBattery ?? 0;
    const batSystemPrice = calculateStoragePrice(battery as Product, amountOfBattery, isFullFeedIn);
    let modulePriceUnit = 0;
    const isModuleUseGlobalSurchage = (module.data as { useGlobalSurchage: boolean }).useGlobalSurchage ?? true;
    let moduleSurcharge = 0;
    if (isModuleUseGlobalSurchage) {
        moduleSurcharge = module.surchargePercentage ?? 0;
    }
    const moduleData = module!.data as ModuleData;

    let pvSystemPrice = 0;
    if (moduleData.usePreisCategory) {
        const ranges =
            moduleData.range ??
            [];
        if (ranges.length > 0) {
            const validRanges = ranges.find((x) => {
                const aboveMin = x.min === undefined || x.min === null || x.min === 0 || configurator.pvSize >= x.min;
                const belowMax = x.max === undefined || x.max === null || x.max === 0 || configurator.pvSize <= x.max;
                return aboveMin && belowMax;
            });
            if (validRanges) {
                modulePriceUnit = validRanges.price;
                if (!isModuleUseGlobalSurchage) {
                    moduleSurcharge = validRanges.surcharge;
                }
            } else {
                const fallbackRange = ranges.find((x) => {
                    const hasMin = x.min !== undefined && x.min !== null && x.min !== 0;
                    return hasMin && x.min >= configurator.pvSize;
                });
                if (fallbackRange) {
                    modulePriceUnit = fallbackRange.price ?? module.price;
                    if (!isModuleUseGlobalSurchage) {
                        moduleSurcharge = fallbackRange.surcharge;
                    }
                }
            }
        }
    } else {
        modulePriceUnit = module.price;
        moduleSurcharge = module.surchargePercentage ?? 0;
    }
    pvSystemPrice = ((module.referenceUnit == ReferenceUnit.UNIT ? configurator.amountOfModule : configurator.pvSize) * modulePriceUnit) * (1 + (moduleSurcharge) / 100);

    let totalPrice = 0;
    totalPrice = pvSystemPrice + batSystemPrice;

    const additionalOptionPreis = configurator.additionOptions.reduce(
        (sum, opt) => sum + (opt.totalPrice ?? 0),
        0
    );

    totalPrice += additionalOptionPreis;
    totalPrice += calculateDefaultItemsPrices(
        products,
        configurator.amountOfModule,
        configurator.pvSize
    );
    totalPrice += calculateSurcharges(
        products,
        totalPrice
    );
    return Math.ceil(totalPrice / 5) * 5;
}

export function calculateBatteryNumber() {
    const configurator = get(useConfiguratorStore());
    const productsStore = useConfiguratorProductsStore();
    const products = get(productsStore);
    const batteries = products.filter((x) => x.type == ProductTypes.BATTERY);
    const battery = batteries.find((x) => x.id == configurator.moduleComponent.batterySystem);

    if (!battery) {
        return 0;
    }
    const batteryData = battery.data as BatteryData;
    const maxBatteryNumber = batteryData.maxBatteries ?? 10;

    const amountOfBattery =
        Math.ceil(
            ((configurator.electricityConsumption.electricityConsumption / 1000) *
                configurator.dimensionFactorBattery) /
            batteryData.batteryCapacity
        );
    return isNaN(amountOfBattery) ? 1 : Math.min(amountOfBattery, maxBatteryNumber);
}

export function calculatePreisWithDiscount(totalPrice: number, discount: number, extraCharge: number = 0, extraChargeType: ExtraChargeType = ExtraChargeType.EURO) {
    if (extraCharge > 0) {
        if (extraChargeType == ExtraChargeType.EURO) {
            return Math.ceil(((totalPrice + extraCharge) * (1 - discount / 100)) / 5) * 5;
        } else {
            return Math.ceil((totalPrice * (1 + extraCharge / 100) * (1 - discount / 100)) / 5) * 5;
        }
    }
    return Math.ceil((totalPrice - totalPrice * (discount / 100)) / 5) * 5;
}

export function calculateAmountOfModules() {
    const configurator = get(useConfiguratorStore());
    const productsStore = useConfiguratorProductsStore();
    const products = get(productsStore);
    const modules = products.filter((x) => x.type == ProductTypes.MODULE);
    const module = modules.find((m) => m.id === configurator.moduleComponent.moduleType);
    if (!module) {
        return 0;
    }
    const powerClass = (module.data as ModuleData).powerClass;
    if (!powerClass || powerClass <= 0) {
        return 0;
    }
    let totalConsumption = 0;
    let totalECarConsumption = 0;
    let amountOfModules = 0;
    const eCarConsume = configurator.eCarConsume ?? 0;
    const isECarActive = eCarConsume > 0;
    if (isECarActive) {
        totalECarConsumption = (eCarConsume / 100) * configuratorConstants.electricVehicle.consumption;
    }
    const totalHeatPumpConsumption =
        (configurator.heatPumpConsume ?? 0) >
            configuratorConstants.heatPump.minYearlyConsumption
            ? configurator.heatPumpConsume
            : 0;
    totalConsumption = totalECarConsumption + totalHeatPumpConsumption + configurator.electricityConsumption.electricityConsumption;
    amountOfModules = Math.trunc((totalConsumption / 1000) * (configurator.dimensionFactorPV ?? 1) / (powerClass / 1000));
    return amountOfModules;
}

type RoofChangeResult = {
    roofSurfaces: RoofSurfaces;
    amountOfModule: number;
};

export function doRoofChange(roofSurfaces: RoofSurfaces): RoofChangeResult | undefined {
    const totalModuleArea = roofSurfaces.modules.reduce((sum, module) => sum + (module.displayModule ?? 0), 0);
    const amountOfModule = calculateAmountOfModules();

    if (amountOfModule <= 0 || roofSurfaces.modules.length === 0) {
        return {
            roofSurfaces,
            amountOfModule: totalModuleArea
        };
    }

    let remainingModules = amountOfModule;
    const updatedModules = roofSurfaces.modules.map((surface, index) => {
        const ratio = (surface.displayModule ?? 0) / totalModuleArea;
        let moduleCount = Math.trunc(amountOfModule * ratio);

        if (index === roofSurfaces.modules.length - 1) {
            moduleCount = remainingModules;
        } else {
            remainingModules -= moduleCount;
        }

        return {
            ...surface,
            moduleCount: moduleCount,
            displayModule: moduleCount
        } as RoofSurface;
    });

    return {
        roofSurfaces: {
            modules: updatedModules
        },
        amountOfModule: amountOfModule
    };
}